/**
 * PlatformSendsPanel — Iter-48
 *
 * Super-admin filterable log of every email / SMS / WhatsApp send made by
 * the platform (admin test sends + background job sends + studio-triggered
 * flows). Mounted on /admin/settings as the "Recent Sends" tab. Backed by
 * GET /api/admin/platform-sends. 30-day retention.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from '../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { RefreshCw, Loader2, Send, Mail, MessageSquare, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';

const channelIcon = (channel) => {
  if (channel === 'email') return <Mail className="w-3.5 h-3.5" />;
  if (channel === 'sms') return <MessageSquare className="w-3.5 h-3.5" />;
  if (channel === 'whatsapp') return <MessageCircle className="w-3.5 h-3.5" />;
  return <Send className="w-3.5 h-3.5" />;
};

const statusVariant = (status) => {
  if (status === 'sent' || status === 'delivered') return 'default';
  if (status === 'failed') return 'destructive';
  return 'secondary';
};

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IE', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
};

const maskRecipient = (recipient) => {
  if (!recipient) return '—';
  // Emails: keep first 2 chars + domain
  if (recipient.includes('@')) {
    const [local, domain] = recipient.split('@');
    if (local.length <= 2) return recipient;
    return `${local.slice(0, 2)}***@${domain}`;
  }
  // Phone: mask middle digits
  if (recipient.length > 6) {
    return `${recipient.slice(0, 4)}***${recipient.slice(-2)}`;
  }
  return recipient;
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (days) =>
  new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

export const PlatformSendsPanel = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // Filters
  const [channel, setChannel] = useState('all');
  const [logStatus, setLogStatus] = useState('all');
  const [recipient, setRecipient] = useState('');
  const [startDate, setStartDate] = useState(daysAgoIso(7));
  const [endDate, setEndDate] = useState(todayIso());

  const fetchLogs = useCallback(
    async (isRefresh = false, overrides = null) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      try {
        const effective = {
          channel: overrides?.channel ?? channel,
          logStatus: overrides?.logStatus ?? logStatus,
          recipient: overrides?.recipient ?? recipient,
          startDate: overrides?.startDate ?? startDate,
          endDate: overrides?.endDate ?? endDate,
        };
        const query = { limit: 200 };
        if (effective.channel !== 'all') query.channel = effective.channel;
        if (effective.logStatus !== 'all') query.log_status = effective.logStatus;
        if (effective.recipient.trim()) query.recipient = effective.recipient.trim();
        if (effective.startDate) query.start_date = effective.startDate;
        if (effective.endDate) query.end_date = effective.endDate;

        const res = await apiGet('/api/admin/platform-sends', { query });
        setLogs(res || []);
      } catch (e) {
        toast.error(formatApiError(e, 'Failed to load platform sends'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [channel, logStatus, recipient, startDate, endDate]
  );

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => fetchLogs(true);

  const clearFilters = () => {
    // Iter-48 fix: pass overrides so we don't rely on the stale closure of
    // fetchLogs; setState is async and setTimeout(fetchLogs) previously
    // captured the pre-clear filter values.
    const defaults = {
      channel: 'all',
      logStatus: 'all',
      recipient: '',
      startDate: daysAgoIso(7),
      endDate: todayIso(),
    };
    setChannel(defaults.channel);
    setLogStatus(defaults.logStatus);
    setRecipient(defaults.recipient);
    setStartDate(defaults.startDate);
    setEndDate(defaults.endDate);
    fetchLogs(true, defaults);
  };

  return (
    <div className="space-y-4" data-testid="platform-sends-panel">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-primary" />
                Recent Platform Sends
              </CardTitle>
              <CardDescription>
                Every email, SMS and WhatsApp dispatched by the platform. 30-day retention.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchLogs(true)}
              disabled={refreshing}
              data-testid="sends-refresh-btn"
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters — Iter-53c: single column below sm so labels + inputs
              aren't crammed to unreadable widths on 390px. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <Label htmlFor="sends-channel" className="text-xs">Channel</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger id="sends-channel" data-testid="sends-channel-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All channels</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sends-status" className="text-xs">Status</Label>
              <Select value={logStatus} onValueChange={setLogStatus}>
                <SelectTrigger id="sends-status" data-testid="sends-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any status</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sends-recipient" className="text-xs">Recipient contains</Label>
              <Input
                id="sends-recipient"
                placeholder="email or phone"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                data-testid="sends-recipient-filter"
              />
            </div>
            <div>
              <Label htmlFor="sends-start" className="text-xs">From</Label>
              <Input
                id="sends-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="sends-start-date"
              />
            </div>
            <div>
              <Label htmlFor="sends-end" className="text-xs">To</Label>
              <Input
                id="sends-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="sends-end-date"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={applyFilters}
              disabled={refreshing}
              data-testid="sends-apply-filters"
            >
              Apply Filters
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={clearFilters}
              disabled={refreshing}
              data-testid="sends-clear-filters"
            >
              Reset
            </Button>
            <span className="text-xs text-muted-foreground ml-auto" data-testid="sends-count">
              {logs.length} record{logs.length === 1 ? '' : 's'}
            </span>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed rounded-lg">
              <Send className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">
                No sends match these filters.
              </p>
            </div>
          ) : (
            <div data-testid="sends-table">
              {/* Iter-53c: below md, render each log as a stacked card. Above
                  md, keep the compact table. Mobile card view surfaces the
                  same info without horizontal scrolling. */}
              <div className="md:hidden space-y-2">
                {logs.map((log, i) => {
                  const rowId = log.id || `${log.sent_at}-${i}`;
                  const isExpanded = expandedId === rowId;
                  return (
                    <div
                      key={rowId}
                      className="border rounded-lg overflow-hidden bg-background"
                      data-testid={`send-card-${i}`}
                    >
                      <button
                        type="button"
                        onClick={() => setExpandedId(isExpanded ? null : rowId)}
                        className="w-full text-left p-3 space-y-2 hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium capitalize">
                            {channelIcon(log.channel)}
                            {log.channel}
                          </span>
                          <Badge variant={statusVariant(log.status)}>
                            {log.status || '—'}
                          </Badge>
                        </div>
                        <div className="text-xs font-mono text-muted-foreground break-all">
                          {maskRecipient(log.recipient)}
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span className="font-mono">{formatTimestamp(log.sent_at)}</span>
                          <span className="truncate ml-2">{log.provider || '—'}</span>
                        </div>
                        {log.event_type && (
                          <div className="text-xs text-muted-foreground truncate">
                            <span className="text-muted-foreground/70">Template:</span> {log.event_type}
                          </div>
                        )}
                      </button>
                      {isExpanded && (
                        <div className="bg-muted/20 border-t p-3 space-y-1.5 text-xs">
                          <div>
                            <span className="font-semibold text-muted-foreground">Full recipient:</span>{' '}
                            <span className="font-mono break-all">{log.recipient || '—'}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-muted-foreground">Studio:</span>{' '}
                            <span className="font-mono">
                              {log.studio_id === '_platform_'
                                ? '(platform test send)'
                                : log.studio_id || '—'}
                            </span>
                          </div>
                          {log.subject && (
                            <div><span className="font-semibold text-muted-foreground">Subject:</span> {log.subject}</div>
                          )}
                          {log.content_preview && (
                            <div className="text-muted-foreground">
                              <span className="font-semibold not-italic">Preview:</span> {log.content_preview}
                            </div>
                          )}
                          {log.message_id && (
                            <div><span className="font-semibold text-muted-foreground">Message ID:</span> <span className="font-mono break-all">{log.message_id}</span></div>
                          )}
                          {log.error_code && (
                            <div><span className="font-semibold text-destructive">Error code:</span> <span className="font-mono">{log.error_code}</span></div>
                          )}
                          {log.error_message && (
                            <div><span className="font-semibold text-destructive">Error:</span> {log.error_message}</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Desktop table (md+) */}
              <div className="hidden md:block border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left p-2 pl-3">When</th>
                      <th className="text-left p-2">Channel</th>
                      <th className="text-left p-2">Recipient</th>
                      <th className="text-left p-2">Provider</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Template</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, i) => {
                      const rowId = log.id || `${log.sent_at}-${i}`;
                      const isExpanded = expandedId === rowId;
                      return (
                        <React.Fragment key={rowId}>
                          <tr
                            className="border-t hover:bg-muted/30 cursor-pointer transition-colors"
                            onClick={() => setExpandedId(isExpanded ? null : rowId)}
                            data-testid={`send-row-${i}`}
                          >
                            <td className="p-2 pl-3 font-mono text-xs whitespace-nowrap">
                              {formatTimestamp(log.sent_at)}
                            </td>
                            <td className="p-2">
                              <span className="inline-flex items-center gap-1.5">
                                {channelIcon(log.channel)}
                                <span className="capitalize">{log.channel}</span>
                              </span>
                            </td>
                            <td className="p-2 font-mono text-xs">
                              {maskRecipient(log.recipient)}
                            </td>
                            <td className="p-2 text-xs text-muted-foreground">
                              {log.provider || '—'}
                            </td>
                            <td className="p-2">
                              <Badge variant={statusVariant(log.status)}>
                                {log.status || '—'}
                              </Badge>
                            </td>
                            <td className="p-2 text-xs text-muted-foreground truncate max-w-[160px]">
                              {log.event_type || '—'}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-muted/20 border-t">
                              <td colSpan={6} className="p-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                  <div>
                                    <span className="font-semibold text-muted-foreground">Full recipient:</span>{' '}
                                    <span className="font-mono">{log.recipient || '—'}</span>
                                  </div>
                                  <div>
                                    <span className="font-semibold text-muted-foreground">Studio:</span>{' '}
                                    <span className="font-mono">
                                      {log.studio_id === '_platform_'
                                        ? '(platform test send)'
                                        : log.studio_id || '—'}
                                    </span>
                                  </div>
                                  {log.subject && (
                                    <div className="md:col-span-2">
                                      <span className="font-semibold text-muted-foreground">Subject:</span>{' '}
                                      {log.subject}
                                    </div>
                                  )}
                                  {log.content_preview && (
                                    <div className="md:col-span-2">
                                      <span className="font-semibold text-muted-foreground">Preview:</span>{' '}
                                      <span className="text-muted-foreground">{log.content_preview}</span>
                                    </div>
                                  )}
                                  {log.message_id && (
                                    <div>
                                      <span className="font-semibold text-muted-foreground">Message ID:</span>{' '}
                                      <span className="font-mono">{log.message_id}</span>
                                    </div>
                                  )}
                                  {log.error_code && (
                                    <div>
                                      <span className="font-semibold text-destructive">Error code:</span>{' '}
                                      <span className="font-mono">{log.error_code}</span>
                                    </div>
                                  )}
                                  {log.error_message && (
                                    <div className="md:col-span-2">
                                      <span className="font-semibold text-destructive">Error:</span>{' '}
                                      <span>{log.error_message}</span>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
